import React, { useCallback, useState } from 'react';
import { Box, Button, LinearProgress } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

/**
 * The Export PDF button, with a progress bar for as long as the export runs.
 *
 * Building a report takes a noticeable moment — images are loaded and drawn, and inside
 * the app wrapper the PDF is uploaded before the download starts — so the click needs
 * visible feedback. Mirrors the Save to History button's bar in HistoryToolbar.
 *
 * `onExport` is awaited, so the bar covers the real work: hand it something that returns
 * the export promise, not a fire-and-forget call.
 */

/** Floor on how long the bar shows, so a fast export doesn't flash it. */
const MIN_FEEDBACK_MS = 600;

interface ExportPdfButtonProps {
  onExport: () => void | Promise<unknown>;
  label?: string;
  disabled?: boolean;
  /** Tutorial anchor — the walkthrough points at `[data-tutorial="…"]`. */
  dataTutorial?: string;
}

export default function ExportPdfButton({
  onExport,
  label = 'Export PDF',
  disabled = false,
  dataTutorial,
}: ExportPdfButtonProps) {
  const [exporting, setExporting] = useState(false);

  const handleClick = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    const startedAt = Date.now();
    try {
      await onExport();
    } catch (error) {
      // The export helpers already report their own failures; never leave the bar spinning.
      // eslint-disable-next-line no-console
      console.error('PDF export failed', error);
    } finally {
      const elapsed = Date.now() - startedAt;
      if (elapsed < MIN_FEEDBACK_MS) {
        await new Promise((resolve) => { window.setTimeout(resolve, MIN_FEEDBACK_MS - elapsed); });
      }
      setExporting(false);
    }
  }, [exporting, onExport]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Button
        variant="outlined"
        size="small"
        startIcon={<PictureAsPdfIcon />}
        onClick={() => void handleClick()}
        disabled={disabled || exporting}
        data-tutorial={dataTutorial}
      >
        {exporting ? 'Exporting…' : label}
      </Button>
      {exporting && <LinearProgress sx={{ height: 4, borderRadius: 2 }} />}
    </Box>
  );
}
